import React, { useEffect, useState, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { supabase } from '../services/supabase';
import { Users, Store, Clock, Activity } from 'lucide-react';

interface PresenceState {
  user_id: string;
  email: string;
  name: string;
  online_at: string;
  last_active_at: number;
  storeName: string;
  branch: string;
}

export default function StatusPage() {
  const { user, useDevBranch } = useAuthStore();
  const [onlineUsers, setOnlineUsers] = useState<PresenceState[]>([]);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    if (!user) return;

    // Conectar ao mesmo canal global utilizado pelo PresenceToaster
    const roomName = 'site-editor-presence-global';
    const channel = supabase.channel(roomName, {
      config: {
        presence: {
          key: user.id,
        },
      },
    });

    channelRef.current = channel;

    // Escutar mudanças de estado de presença (entrada/saída/atualização)
    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const users: PresenceState[] = [];
      
      for (const id in state) {
        const presence = state[id][0] as PresenceState;
        if (presence) {
          users.push(presence);
        }
      }
      
      // Ordenar por loja e depois por nome
      users.sort((a, b) => {
        if (a.storeName !== b.storeName) return a.storeName.localeCompare(b.storeName);
        return (a.name || a.email).localeCompare(b.name || b.email);
      });

      setOnlineUsers(users);
    });

    // Inscrever-se no canal
    channel.subscribe((status: any) => {
      if (status === 'SUBSCRIBED') {
         // Opcional: Se não quisermos aparecer na lista, basta comentar esse channel.track().
         // Abaixo ajustado para usar a loja/branch correta
         const _ = channel.track({
          user_id: user.id,
          email: user.email,
          name: user.name || user.email?.split('@')[0] || 'Usuário',
          online_at: new Date().toISOString(),
          last_active_at: Date.now(),
          storeName: user.storeName || 'unknown',
          branch: useDevBranch ? 'site-editor' : 'main',
        });
      }
    });

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe();
      }
    };
  }, [user]);

  // Forçar atualização do cronômetro de tempo inativo visual
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center">
            <Activity className="w-6 h-6 mr-2 text-primary-500" />
            Status de Sistema
          </h1>
          <p className="text-gray-500 mt-1">
            Visualização de usuários atualmente conectados e lojas sendo editadas.
          </p>
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        <div className="grid grid-cols-[300px_1fr_1fr_1fr_200px] bg-gray-50 p-4 border-b font-medium text-sm text-gray-700">
          <div className="col-span-1 flex items-center"><Users className="w-4 h-4 mr-2"/>Usuário</div>
          <div className="col-span-1 flex items-center"><Store className="w-4 h-4 mr-2"/>Loja</div>
          <div className="col-span-1 flex items-center"><Activity className="w-4 h-4 mr-2"/>Branch</div>
          <div className="col-span-1 flex items-center"><Activity className="w-4 h-4 mr-2"/>Modo</div>
          <div className="col-span-1 flex items-center"><Clock className="w-4 h-4 mr-2"/>Última Ação</div>
        </div>
        
        {onlineUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            Nenhum usuário ativo identificado no momento.
          </div>
        ) : (
          <div className="divide-y">
            {onlineUsers.map((user, idx) => {
              const diffMins = Math.floor((Date.now() - user.last_active_at) / 60000);
              const isInactive = diffMins >= 10;
              
              return (
              <div key={idx} className="grid grid-cols-[300px_1fr_1fr_1fr_200px] p-4 text-sm items-center hover:bg-gray-50 transition">
                <div className="col-span-1 font-medium text-gray-900 truncate pr-4 flex items-center" title={user.email}>
                  <div className="relative flex items-center justify-center mr-3">
                    <div className={`w-2 h-2 rounded-full ${isInactive ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    {!isInactive && (
                      <div className="absolute w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75"></div>
                    )}
                  </div>
                  <span className="truncate">{user.email}</span>
                </div>
                <div className="col-span-1">
                  <span className="px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                    {user.storeName}
                  </span>
                </div>
                <div className="col-span-1 font-mono text-xs text-gray-600">
                  {user.branch}
                </div>
                <div className="col-span-1">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    user.branch === 'site-editor' 
                      ? 'bg-amber-50 text-amber-700' 
                      : 'bg-emerald-50 text-emerald-700'
                  }`}>
                    {user.branch === 'site-editor' ? 'Modo Dev' : 'Modo Normal'}
                  </span>
                </div>
                <div className="col-span-1 text-gray-500">
                  <div className="flex flex-col">
                    <span>{new Date(user.last_active_at).toLocaleString('pt-BR')}</span>
                    <span className={`text-xs font-medium mt-0.5 ${isInactive ? 'text-red-500' : 'text-transparent select-none'}`}>
                      {isInactive ? `Inativo há ${diffMins}m` : 'Ativo agora'}
                    </span>
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}
      </div>
    </div>
  );
}
